import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { KeyUri } from '../key';
import { 
  MapPin, Clock, ArrowRight, Heart, 
  ChevronRight, ChevronLeft, Users, 
  Mountain, Palmtree 
} from 'lucide-react';


/* -------------------------------- */
/* CARD COMPONENT (Image Slider) */
/* -------------------------------- */

const FeaturedCard = ({ item, accentColor, wishlisted, toggleWishlist, type }) => {

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const allImages = (() => {
    const imgs = [];

    if (Array.isArray(item.image)) {
      item.image.forEach(i => i && imgs.push(i));
    } else if (item.image) {
      imgs.push(item.image);
    }

    if (Array.isArray(item.image_url)) {
      item.image_url.forEach(i => i && imgs.push(i));
    }

    return imgs.length ? imgs : ['/placeholder-tour.jpg'];
  })();

  const hasMultipleImages = allImages.length > 1;

  return (
    <Link 
      to={type === 'wellness' ? `/wellness/${item._id}` : `/tours/${item._id}`}
      className="group flex-shrink-0 w-[280px] md:w-[350px] snap-start bg-white rounded-[2rem] overflow-hidden border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-500"
    >
      <div className="relative aspect-[4/3] overflow-hidden">

        {/* IMAGE SLIDER */}
        <div
          className="flex h-full transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
        >
          {allImages.map((img, idx) => (
            <div key={idx} className="w-full h-full flex-shrink-0">
              <img
                src={img}
                alt={item.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                onError={(e) => { e.target.src = "/placeholder-tour.jpg"; }}
              />
            </div>
          ))}
        </div>

        {/* LEFT ARROW */}
        {hasMultipleImages && currentImageIndex > 0 && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setCurrentImageIndex(i => i - 1);
            }}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 rounded-full shadow flex items-center justify-center z-20"
          >
            <ChevronLeft size={18} />
          </button>
        )}

        {/* RIGHT ARROW */}
        {hasMultipleImages && currentImageIndex < allImages.length - 1 && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setCurrentImageIndex(i => i + 1);
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 rounded-full shadow flex items-center justify-center z-20"
          >
            <ChevronRight size={18} />
          </button>
        )}

        {/* CATEGORY TAG */}
        <div className="absolute top-4 left-4">
          <span className="bg-white/90 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter text-slate-900">
            {item.category || item.tag || 'Featured'}
          </span>
        </div>

        {/* WISHLIST */}
        <button 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleWishlist(item._id);
          }}
          className="absolute top-4 right-4 bg-black/20 backdrop-blur-md p-2 rounded-full text-white hover:text-red-500 transition-colors"
        >
          <Heart size={18} fill={wishlisted.includes(item._id) ? "currentColor" : "none"} />
        </button>

        {/* LOCATION */}
        {item.location && (
          <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent p-6">
            <div className="flex items-center gap-2 text-white/90 text-[10px] font-bold uppercase tracking-widest">
              <MapPin size={12} />
              {typeof item.location === "string" ? item.location : item.location.city}
            </div>
          </div>
        )}
      </div>

      {/* CARD BODY */}
      <div className="p-6">

        <h3 className="text-lg font-bold text-slate-900 leading-tight mb-3 line-clamp-2">
          {item.title}
        </h3>

        <div className="space-y-1 mb-4 text-slate-500 text-xs font-semibold">

          <div className="flex items-center gap-3 pt-1">
            <div className="flex items-center gap-1">
              <Clock size={14} />
              <span>{item.duration || '4 Days'}</span>
            </div>
          </div>

          {item.location && (
            <div className="flex items-center gap-1">
              <MapPin size={14} />
              <span>{typeof item.location === "string" ? item.location : item.location.city}</span>
            </div>
          )}

          {item.available_slots && (
            <div className="flex items-center gap-1">
              <Users size={14} />
              <span className="font-bold text-slate-900">{item.available_slots} slots</span>
            </div>
          )}

        </div>

        {/* PRICING */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-50">

          <div>
            {(() => {

              const price =
                item.display_price ||
                item.starting_program_price ||
                item.price ||
                item.final_price_with_lowest_stay ||
                0;

              const original = item.original_price;

              return (
                <>
                  {original && original > price && (
                    <div className="text-sm text-slate-400 line-through">
                      ₹{Number(original).toLocaleString("en-IN")}
                    </div>
                  )}

                  <span className="text-xl font-black text-slate-900">
                    ₹{Number(price).toLocaleString("en-IN")}
                  </span>
                </>
              );

            })()}
          </div>

          <button
            className={`text-white font-bold py-2 px-4 rounded-xl text-xs transition-all active:scale-95 ${accentColor.replace("text-", "bg-")} hover:opacity-90`}
          >
            View Details
          </button>

        </div>
      </div>
    </Link>
  );
};


/* -------------------------------- */
/* SCROLL SECTION */
/* -------------------------------- */

const ScrollSection = ({ title, subtitle, items, accentColor, icon: Icon, type, linkTo }) => {

  const scrollContainerRef = useRef(null);
  const [wishlisted, setWishlisted] = useState([]);

  const toggleWishlist = (id) => {
    setWishlisted(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const scroll = (direction) => {

    if (!scrollContainerRef.current) return;

    const { scrollLeft, clientWidth } = scrollContainerRef.current;

    const scrollTo =
      direction === "left"
        ? scrollLeft - clientWidth
        : scrollLeft + clientWidth;

    scrollContainerRef.current.scrollTo({
      left: scrollTo,
      behavior: "smooth"
    });

  };

  return (
    <div className="mb-20">

      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-6">

        <div>
          <div className={`flex items-center gap-2 font-bold text-sm tracking-widest uppercase ${accentColor}`}>
            <Icon size={18} />
            {subtitle}
          </div>

          <h2 className="text-3xl md:text-4xl font-black text-slate-900">
            {title}
          </h2>
        </div>

        <div className="flex gap-3">
          <button onClick={() => scroll("left")} className="p-2 border rounded-full">
            <ChevronLeft size={22} />
          </button>
          <button onClick={() => scroll("right")} className="p-2 border rounded-full">
            <ChevronRight size={22} />
          </button>
        </div>

      </div>

      {/* SCROLLER */}
      <div
        ref={scrollContainerRef}
        className="flex overflow-x-auto gap-6 pb-8 snap-x snap-mandatory no-scrollbar"
      >

        {items.map((item) => (
          <FeaturedCard
            key={item._id}
            item={item}
            accentColor={accentColor}
            wishlisted={wishlisted}
            toggleWishlist={toggleWishlist}
            type={type}
          />
        ))}

        {/* SEE ALL CARD */}
        <Link
          to={linkTo}
          className="flex-shrink-0 w-[160px] snap-start flex flex-col items-center justify-center bg-slate-50 rounded-[2rem] border-2 border-dashed border-slate-200"
        >
          <ArrowRight size={20} />
          <span className="text-xs font-bold text-slate-500">See All</span>
        </Link>

      </div>

    </div>
  );
};


/* -------------------------------- */
/* MAIN HOME COMPONENT */
/* -------------------------------- */

const FeaturedHome = () => {

  const [featuredTours, setFeaturedTours] = useState([]);
  const [featuredRetreats, setFeaturedRetreats] = useState([]);

  useEffect(() => {

    const fetchFeatured = async () => {

      try {

        const [toursRes, retreatsRes] = await Promise.all([
          axios.get(`${KeyUri.BACKEND_URI}/get-tours?featured=true`),
          axios.get(`${KeyUri.BACKEND_URI}/wellness-packages?featured=true`)
        ]);

        setFeaturedTours(toursRes.data.slice(0, 8));
        setFeaturedRetreats(retreatsRes.data.slice(0, 8));

      } catch (err) {
        console.error(err);
      }

    };

    fetchFeatured();

  }, []);

  if (!featuredTours.length && !featuredRetreats.length) return null;

  return (
    <section className="w-full bg-gradient-to-b from-blue-50 to-indigo-50 py-16">

      <div className="max-w-8xl mx-auto px-4 md:px-12 lg:px-24">

        {featuredTours.length > 0 && (
          <ScrollSection
            title="Featured Tours"
            subtitle="Explore the World"
            items={featuredTours}
            accentColor="text-blue-600"
            icon={Mountain}
            type="tour"
            linkTo="/packages#tours-section"
          />
        )}

        {featuredRetreats.length > 0 && (
          <ScrollSection
            title="Premium Retreats"
            subtitle="Wellness & Serenity"
            items={featuredRetreats}
            accentColor="text-emerald-600"
            icon={Palmtree}
            type="wellness"
            linkTo="/wellness"
          />
        )}

      </div>

    </section>
  );
};

export default FeaturedHome;